import { Typography, useTheme } from "@mui/material";
import Box, { type BoxProps } from "@mui/material/Box";
import Image from "next/image";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type ImageOverlayProps = {
  children?: ReactNode;
  overlaySlot?: ReactNode;
  imageSrc: string;
  altText: string;
  emptyStateText?: string;
  blurDataUrl?: string | null | undefined;
  height: number;
  width: number;
  onClick?: () => void;
  placeholderBoxProps?: BoxProps;
  component?: React.ElementType;
};

export function ImageOverlay({
  children,
  overlaySlot,
  imageSrc,
  altText,
  blurDataUrl,
  emptyStateText,
  height,
  width,
  onClick,
  placeholderBoxProps,
  component = "div",
}: ImageOverlayProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ImageOverlay",
  });
  const defaultEmptyStateText = t(
    "uploadImageText",
    "Click to upload an image"
  );
  const theme = useTheme();
  return (
    <Box
      component={component}
      display="grid"
      gridTemplateAreas='"stack"'
      onClick={onClick}
      sx={{
        cursor: "pointer",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
      }}
    >
      <Box
        sx={{
          gridArea: "stack",
          display: "grid",
          placeItems: "center",
          transition: "backdrop-filter ease-in-out 0.3s, opacity 0.3s ",
          backdropFilter: "brightness(100%)",
          opacity: 0,
          zIndex: 2,
          "&:hover": {
            backdropFilter: "brightness(40%)",
            opacity: 1,
          },
        }}
      >
        {overlaySlot}
      </Box>
      {imageSrc ? (
        <Image
          src={imageSrc ?? ""}
          alt={altText}
          blurDataURL={blurDataUrl ?? ""}
          placeholder={blurDataUrl ? "empty" : "blur"}
          height={height}
          width={width}
          style={{
            objectFit: "cover",
            height: "auto",
            width: "100%",
            gridArea: "stack",
          }}
          unoptimized
        />
      ) : (
        <Box
          {...placeholderBoxProps}
          sx={{
            height: "auto",
            width: "100%",
            minHeight: 100,
            backgroundColor: "grey.300",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gridArea: "stack",
            ...placeholderBoxProps?.sx,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            maxWidth="75%"
            component={"pre"}
            textAlign={"center"}
            whiteSpace={"pre-wrap"}
          >
            {emptyStateText ?? defaultEmptyStateText}
          </Typography>
        </Box>
      )}
      <Box sx={{ gridArea: "stack" }}>{children}</Box>
    </Box>
  );
}
